// Catalog of SEO landing pages.
// We generate routes like /offertes/{branche-slug} and /offertes/{branche-slug}/{stad-slug}
// Each landing page is content-rich, GEO-targeted, with crisp meta tags + JSON-LD.
//
// Keep this in sync with backend `seo.py` sitemap generation if you add new
// branches or cities.

import { Hammer, Zap, Paintbrush, Trees, Laptop2, Truck, Wrench, Sparkles } from "lucide-react";

// Canonical branche list — matches the wizard's TYPES so the CTA pre-fills
// the analyse wizard with the correct branche.
export const BRANCHES = [
  {
    slug: "renovatie-en-bouw",
    name: "Renovatie & Bouw",
    name_lower: "renovatie & bouw",
    wizard_type: "Renovatie & Bouw",
    icon: Hammer,
    h1Suffix: "renovatie- of bouwofferte",
    intro: "Verbouwing, uitbreiding, nieuwbouw of badkamer-renovatie — onze AI ontleedt elke post (materiaal, arbeid, opkuis, BTW) en toont je in 60 seconden of de prijs marktconform is.",
    examples: ["Keukenrenovatie", "Badkamer", "Dakrenovatie", "Uitbouw / aanbouw", "Gevelisolatie", "Volledige renovatie"],
    tips: [
      "Vraag een afzonderlijke prijs voor materiaal en arbeidsuren.",
      "Laat afbraak en afvalcontainer apart vermelden — daar zit vaak verborgen marge.",
      "Vraag naar de gehanteerde uurprijs en hoeveel mensen er werken.",
    ],
  },
  {
    slug: "elektriciteit-en-sanitair",
    name: "Elektriciteit & Sanitair",
    name_lower: "elektriciteit & sanitair",
    wizard_type: "Elektriciteit & Sanitair",
    icon: Zap,
    h1Suffix: "elektriciteits- of loodgietersofferte",
    intro: "Van een nieuwe zekeringkast tot een complete sanitaire renovatie. We vergelijken elk onderdeel met actuele markttarieven (België & Nederland) en signaleren ontbrekende posten.",
    examples: ["Volledige keuring elektriciteit", "Plaatsing zekeringkast", "Sanitair badkamer", "Cv-ketel vervangen", "Lekkage herstellen", "Domotica installatie"],
    tips: [
      "Eis een attest van keuring voor elektriciteit — verplicht bij verkoop en renovatie.",
      "Vraag het merk + model van de gekozen toestellen (boiler, kraan, …).",
      "Let op verschillende uurtarieven voor loodgieter vs. elektricien.",
    ],
  },
  {
    slug: "schilderwerk-en-afwerking",
    name: "Schilderwerk & Afwerking",
    name_lower: "schilderwerk & afwerking",
    wizard_type: "Schilderwerk & Afwerking",
    icon: Paintbrush,
    h1Suffix: "schilder- of afwerkingsofferte",
    intro: "Binnen of buiten, één kleur of meer — schilderwerk wordt vaak in m² gefactureerd. Wij rekenen voor jou na of de aangerekende oppervlakte en lagen kloppen.",
    examples: ["Binnenmuren schilderen", "Buitengevel", "Plafonds", "Houtwerk / ramen", "Vloer leggen", "Plamuren en gladmaken"],
    tips: [
      "Vraag minstens 2 lagen verf (grondlaag + afwerklaag) in de offerte.",
      "Controleer of behangverwijdering of plamuren inbegrepen is.",
      "Let op het aangerekende aantal m² — meet zelf na in twijfelgevallen.",
    ],
  },
  {
    slug: "tuinaanleg-en-buitenwerken",
    name: "Tuinaanleg & Buitenwerken",
    name_lower: "tuinaanleg & buitenwerken",
    wizard_type: "Tuinaanleg & Buitenwerken",
    icon: Trees,
    h1Suffix: "tuin- of buitenwerkenofferte",
    intro: "Terras, oprit, omheining, beplanting of een complete tuinmetamorfose. We vergelijken materialen (keramische tegels, klinkers, hout) én arbeidstijden met de markt.",
    examples: ["Terras keramische tegels", "Oprit klinkers", "Houten omheining", "Gazon aanleggen", "Tuinhuis plaatsen", "Drainage / nivellering"],
    tips: [
      "Vraag de exacte hoeveelheid en herkomst van de tegels of klinkers.",
      "Controleer of de fundering en drainage inbegrepen zijn.",
      "Let op meerkost voor afvoer van grond/oude materialen.",
    ],
  },
  {
    slug: "it-en-technologie",
    name: "IT & Technologie",
    name_lower: "IT & technologie",
    wizard_type: "IT & Technologie",
    icon: Laptop2,
    h1Suffix: "IT- of softwareofferte",
    intro: "Website, webshop, custom software, hardware-rollout of netwerk. IT-offertes zijn vaak ondoorzichtig — wij ontleden licentie, hosting, uren en SLA's.",
    examples: ["Maatwerk website", "Webshop (Shopify / WooCommerce)", "Mobile app", "Cloud migratie", "ERP / boekhouding", "Netwerkinstallatie"],
    tips: [
      "Vraag een afzonderlijke uurprijs voor design vs. development.",
      "Eis een schatting van het aantal uren per onderdeel (geen pauschaal).",
      "Let op terugkerende kosten (hosting, SLA, licenties) — dat verschilt sterk per leverancier.",
    ],
  },
  {
    slug: "verhuizing-en-transport",
    name: "Verhuizing & Transport",
    name_lower: "verhuizing & transport",
    wizard_type: "Verhuizing & Transport",
    icon: Truck,
    h1Suffix: "verhuis- of transportofferte",
    intro: "Verhuisfirma's variëren sterk in prijs. We controleren uurtarief, het aantal verhuizers, kilometerprijs en bijkomende diensten (lift, demontage, opslag).",
    examples: ["Privéverhuis appartement", "Verhuis met lift", "Internationale verhuis", "Opslag (self-storage)", "Piano verhuizen", "Kantoorverhuis"],
    tips: [
      "Vraag of de offerte een vast bedrag of een uurprijs is, en wat er gebeurt bij uitloop.",
      "Controleer of demontage / montage van meubels inbegrepen is.",
      "Let op meerkost voor verhuislift, parkeerplaats reserveren of trappen.",
    ],
  },
  {
    slug: "onderhoud-en-herstelling",
    name: "Onderhoud & Herstelling",
    name_lower: "onderhoud & herstelling",
    wizard_type: "Onderhoud & Herstelling",
    icon: Wrench,
    h1Suffix: "onderhouds- of herstellingsofferte",
    intro: "Onderhoudscontracten (verwarming, airco, dakgoten) zijn vaak jaarlijks terugkerend. We checken of het tarief en de inbegrepen prestaties marktconform zijn.",
    examples: ["Onderhoud cv-ketel", "Airco service", "Dakgoten reinigen", "Schoorsteenveger", "Warmtepomp service", "Reparatie elektriciteit"],
    tips: [
      "Vraag wat exact inbegrepen is: enkel controle of ook vervangstukken?",
      "Controleer de duurtijd van het contract en de opzegtermijn.",
      "Vergelijk verplicht onderhoud (cv ≥ 20kW = jaarlijks) met optionele service.",
    ],
  },
  {
    slug: "andere-offertes",
    name: "Andere offertes",
    name_lower: "andere offertes",
    wizard_type: "Andere",
    icon: Sparkles,
    h1Suffix: "offerte",
    intro: "Geen standaard branche? Geen probleem. Onze AI analyseert ook offertes voor coaching, advocaten, organisators, marketingbureaus, fotografen en alles ertussenin.",
    examples: ["Marketingbureau", "Advocaat / notaris", "Fotograaf / videograaf", "Event-organisator", "Coaching / opleiding", "Boekhouder"],
    tips: [
      "Vraag een gedetailleerde tijdsinschatting (uren per fase).",
      "Eis schriftelijk welke leveringen of deliverables je krijgt.",
      "Vergelijk minstens 2-3 alternatieve aanbiedingen.",
    ],
  },
];

// Major cities in BE (top 30, geordend op SEO-volume) + NL (top 30)
export const CITIES_BE = [
  { slug: "antwerpen", name: "Antwerpen", province: "Antwerpen", country: "BE" },
  { slug: "gent", name: "Gent", province: "Oost-Vlaanderen", country: "BE" },
  { slug: "brussel", name: "Brussel", province: "Brussel", country: "BE" },
  { slug: "brugge", name: "Brugge", province: "West-Vlaanderen", country: "BE" },
  { slug: "leuven", name: "Leuven", province: "Vlaams-Brabant", country: "BE" },
  { slug: "mechelen", name: "Mechelen", province: "Antwerpen", country: "BE" },
  { slug: "hasselt", name: "Hasselt", province: "Limburg", country: "BE" },
  { slug: "kortrijk", name: "Kortrijk", province: "West-Vlaanderen", country: "BE" },
  { slug: "oostende", name: "Oostende", province: "West-Vlaanderen", country: "BE" },
  { slug: "aalst", name: "Aalst", province: "Oost-Vlaanderen", country: "BE" },
  { slug: "sint-niklaas", name: "Sint-Niklaas", province: "Oost-Vlaanderen", country: "BE" },
  { slug: "roeselare", name: "Roeselare", province: "West-Vlaanderen", country: "BE" },
  { slug: "genk", name: "Genk", province: "Limburg", country: "BE" },
  { slug: "turnhout", name: "Turnhout", province: "Antwerpen", country: "BE" },
  { slug: "dendermonde", name: "Dendermonde", province: "Oost-Vlaanderen", country: "BE" },
  { slug: "vilvoorde", name: "Vilvoorde", province: "Vlaams-Brabant", country: "BE" },
  { slug: "tienen", name: "Tienen", province: "Vlaams-Brabant", country: "BE" },
  { slug: "lier", name: "Lier", province: "Antwerpen", country: "BE" },
  { slug: "lokeren", name: "Lokeren", province: "Oost-Vlaanderen", country: "BE" },
  { slug: "ieper", name: "Ieper", province: "West-Vlaanderen", country: "BE" },
  { slug: "waregem", name: "Waregem", province: "West-Vlaanderen", country: "BE" },
  { slug: "geel", name: "Geel", province: "Antwerpen", country: "BE" },
  { slug: "sint-truiden", name: "Sint-Truiden", province: "Limburg", country: "BE" },
  { slug: "beringen", name: "Beringen", province: "Limburg", country: "BE" },
  { slug: "knokke-heist", name: "Knokke-Heist", province: "West-Vlaanderen", country: "BE" },
  { slug: "namen", name: "Namen", province: "Namen", country: "BE" },
  { slug: "luik", name: "Luik", province: "Luik", country: "BE" },
  { slug: "charleroi", name: "Charleroi", province: "Henegouwen", country: "BE" },
  { slug: "doornik", name: "Doornik", province: "Henegouwen", country: "BE" },
  { slug: "bergen", name: "Bergen", province: "Henegouwen", country: "BE" },
];

export const CITIES_NL = [
  { slug: "amsterdam", name: "Amsterdam", province: "Noord-Holland", country: "NL" },
  { slug: "rotterdam", name: "Rotterdam", province: "Zuid-Holland", country: "NL" },
  { slug: "den-haag", name: "Den Haag", province: "Zuid-Holland", country: "NL" },
  { slug: "utrecht", name: "Utrecht", province: "Utrecht", country: "NL" },
  { slug: "eindhoven", name: "Eindhoven", province: "Noord-Brabant", country: "NL" },
  { slug: "tilburg", name: "Tilburg", province: "Noord-Brabant", country: "NL" },
  { slug: "groningen", name: "Groningen", province: "Groningen", country: "NL" },
  { slug: "almere", name: "Almere", province: "Flevoland", country: "NL" },
  { slug: "breda", name: "Breda", province: "Noord-Brabant", country: "NL" },
  { slug: "nijmegen", name: "Nijmegen", province: "Gelderland", country: "NL" },
  { slug: "apeldoorn", name: "Apeldoorn", province: "Gelderland", country: "NL" },
  { slug: "haarlem", name: "Haarlem", province: "Noord-Holland", country: "NL" },
  { slug: "arnhem", name: "Arnhem", province: "Gelderland", country: "NL" },
  { slug: "enschede", name: "Enschede", province: "Overijssel", country: "NL" },
  { slug: "maastricht", name: "Maastricht", province: "Limburg", country: "NL" },
  { slug: "amersfoort", name: "Amersfoort", province: "Utrecht", country: "NL" },
  { slug: "zaanstad", name: "Zaanstad", province: "Noord-Holland", country: "NL" },
  { slug: "den-bosch", name: "'s-Hertogenbosch", province: "Noord-Brabant", country: "NL" },
  { slug: "leiden", name: "Leiden", province: "Zuid-Holland", country: "NL" },
  { slug: "zoetermeer", name: "Zoetermeer", province: "Zuid-Holland", country: "NL" },
  { slug: "zwolle", name: "Zwolle", province: "Overijssel", country: "NL" },
  { slug: "dordrecht", name: "Dordrecht", province: "Zuid-Holland", country: "NL" },
  { slug: "delft", name: "Delft", province: "Zuid-Holland", country: "NL" },
  { slug: "venlo", name: "Venlo", province: "Limburg", country: "NL" },
  { slug: "deventer", name: "Deventer", province: "Overijssel", country: "NL" },
  { slug: "sittard-geleen", name: "Sittard-Geleen", province: "Limburg", country: "NL" },
  { slug: "hilversum", name: "Hilversum", province: "Noord-Holland", country: "NL" },
  { slug: "alkmaar", name: "Alkmaar", province: "Noord-Holland", country: "NL" },
  { slug: "heerlen", name: "Heerlen", province: "Limburg", country: "NL" },
  { slug: "lelystad", name: "Lelystad", province: "Flevoland", country: "NL" },
];

export const ALL_CITIES = [...CITIES_BE, ...CITIES_NL];

export function findBranche(slug) {
  return BRANCHES.find((b) => b.slug === slug);
}

export function findCity(slug) {
  return ALL_CITIES.find((c) => c.slug === slug);
}

// All routes for sitemap consumption (frontend + backend can both consume)
export function listAllLandingRoutes() {
  const routes = [];
  for (const b of BRANCHES) {
    routes.push(`/offertes/${b.slug}`);
    for (const c of ALL_CITIES) {
      routes.push(`/offertes/${b.slug}/${c.slug}`);
    }
  }
  return routes;
}
